// src/components/tutorials/JS_Named_Function.jsx
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Named_Function() {
  const refs = {
    declaration: useRef(null),
    expression: useRef(null),
    hoisting: useRef(null),
    namingRules: useRef(null),
    recursion: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const keyPoints = [
    { key: "declaration", label: "Function Declaration" },
    { key: "expression", label: "Named Function Expression" },
    { key: "hoisting", label: "Hoisting Behavior" },
    { key: "namingRules", label: "Naming Rules" },
    { key: "recursion", label: "Named Function Recursion" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">⚙️ JavaScript Named Functions</h1>
      <p className="tutorial-intro">
        Named functions are functions that have an explicit name. They are
        useful for recursion, debugging, and code clarity. This tutorial covers
        all the main use-cases for named functions.
      </p>

      {/* 🔑 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1.5rem 2.5rem",
          borderRadius: "12px",
          marginTop: "1rem",
          marginBottom: "3rem",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.5rem",
          }}
        >
          {keyPoints.map((item) => (
            <p key={item.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(item.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "black",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {item.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ Function Declaration */}
      <div ref={refs.declaration} className="tutorial-section">
        <h3>1️⃣ Function Declaration</h3>
        <p>
          A named function declared using the{" "}
          <code className="highlight_code">function</code> keyword. These are
          hoisted to the top of their scope.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function greet(name) {
  return "Hello " + name;
}

console.log(greet("Alice")); // Hello Alice`}
          />
          <TryItButton
            code={`function greet(name){return "Hello "+name;}console.log(greet("Alice"));`}
          />
        </div>
        <OutputBox>Output: Hello Alice</OutputBox>
      </div>

      {/* 2️⃣ Named Function Expression */}
      <div ref={refs.expression} className="tutorial-section">
        <h3>2️⃣ Named Function Expression</h3>
        <p>
          A function expression can also have a name. The name is local to the
          function’s own scope and can be useful for recursion or debugging.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const factorial = function fact(n) {
  return n <= 1 ? 1 : n * fact(n - 1);
};

console.log(factorial(5)); // 120`}
          />
          <TryItButton
            code={`const factorial=function fact(n){return n<=1?1:n*fact(n-1);};console.log(factorial(5));`}
          />
        </div>
        <OutputBox>Output: 120</OutputBox>
      </div>

      {/* 3️⃣ Hoisting */}
      <div ref={refs.hoisting} className="tutorial-section">
        <h3>3️⃣ Hoisting Behavior</h3>
        <p>
          Named function <b>declarations</b> are hoisted, so they can be called
          before being defined. Named function <b>expressions</b> are not
          hoisted.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(add(3, 2)); // Works

function add(a, b) {
  return a + b;
}

// console.log(subtract(3,2)); ❌ Error
const subtract = function sub(a, b) {
  return a - b;
};`}
          />
          <TryItButton
            code={`console.log(add(3,2));function add(a,b){return a+b;}try{console.log(subtract(3,2));}catch(e){console.log("Error:",e.message);}const subtract=function sub(a,b){return a-b;};`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            5
            <br />
            Error: Cannot access 'subtract' before initialization
          </pre>
        </OutputBox>
      </div>

      {/* 4️⃣ Naming Rules */}
      <div ref={refs.namingRules} className="tutorial-section">
        <h3>4️⃣ Naming Rules</h3>
        <p>
          Function names must follow valid identifier rules: they can include
          letters, digits, <code>_</code>, and <code>$</code> — but cannot start
          with a digit or be a reserved keyword.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function _myFunc() {
  return "Valid function name";
}

function $calculate() {
  return "Also valid";
}

// ❌ Invalid
// function 1stFunc() {} // SyntaxError`}
          />
          <TryItButton
            code={`function _myFunc(){return "Valid function name";}function $calculate(){return "Also valid";}console.log(_myFunc());console.log($calculate());`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>Valid function name {"\n"}Also valid</pre>
        </OutputBox>
      </div>

      {/* 5️⃣ Named Function Recursion */}
      <div ref={refs.recursion} className="tutorial-section">
        <h3>5️⃣ Named Function Recursion</h3>
        <p>
          Named functions can call themselves recursively, which is useful for
          operations like factorials, Fibonacci, etc.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function countdown(n) {
  if (n <= 0) {
    console.log("Done!");
  } else {
    console.log(n);
    countdown(n - 1);
  }
}

countdown(3);`}
          />
          <TryItButton
            code={`function countdown(n){if(n<=0){console.log("Done!");}else{console.log(n);countdown(n-1);}}countdown(3);`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            3
            <br />
            2
            <br />
            1
            <br />
            Done!
          </pre>
        </OutputBox>
      </div>

      {/* 🚀 Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Compare named vs anonymous functions in debugging stack traces.
          </li>
          <li>
            Explore how naming helps in recursion and self-reference inside
            functions.
          </li>
          <li>
            Try mixing named functions with higher-order functions and closures.
          </li>
        </ul>
      </div>
    </div>
  );
}
